import type { AxiosInstance } from 'axios';

export type SupportTicketStatus =
  | 'OPEN'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'WAITING_FOR_CUSTOMER'
  | 'PENDING_CUSTOMER'
  | 'ESCALATED'
  | 'RESOLVED'
  | 'CLOSED';
export type SupportTicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type SupportTicketCategory =
  | 'ORDER_ISSUE'
  | 'PAYMENT_ISSUE'
  | 'REFUND_ISSUE'
  | 'RETURN_REQUEST'
  | 'WRONG_PRODUCT'
  | 'DAMAGED_PRODUCT'
  | 'DELIVERY_DELAY'
  | 'TECHNICAL_ISSUE'
  | 'OTHER'
  // legacy
  | 'ORDER'
  | 'PAYMENT'
  | 'DELIVERY'
  | 'RETURN_REFUND'
  | 'ACCOUNT'
  | 'PRODUCT'
  | 'VENDOR';

export type SupportActivityKind =
  | 'CREATED'
  | 'ASSIGNED'
  | 'STATUS_CHANGED'
  | 'PRIORITY_CHANGED'
  | 'REPLY_CUSTOMER'
  | 'REPLY_VENDOR'
  | 'REPLY_ADMIN'
  | 'ATTACHMENT_ADDED'
  | 'ESCALATED'
  | 'RESOLVED'
  | 'REOPENED'
  | 'CLOSED'
  | 'SLA_BREACHED'
  | 'NOTE_INTERNAL';

export interface SupportTicketMessage {
  id: string;
  ticketId: string;
  authorId: string;
  authorRole: string;
  body: string;
  attachments: unknown | null;
  internal: boolean;
  createdAt: string;
}

export interface SupportTicketAttachment {
  id: string;
  ticketId: string;
  messageId: string | null;
  uploaderId: string;
  url: string;
  filename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

export interface SupportTicketActivityLog {
  id: string;
  ticketId: string;
  actorId: string | null;
  actorRole: string;
  kind: SupportActivityKind;
  payload: unknown | null;
  note: string | null;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  ticketNumber: string;
  subjectUserId: string;
  assignedToId: string | null;
  vendorId: string | null;
  orderGroupId: string | null;
  orderItemId: string | null;
  productId: string | null;
  category: SupportTicketCategory;
  subject: string;
  description: string | null;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  refType: string | null;
  refId: string | null;
  tags: string[];
  slaDueAt: string | null;
  slaBreachedAt: string | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  escalatedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportTicketDetail extends SupportTicket {
  messages: SupportTicketMessage[];
  attachments: SupportTicketAttachment[];
  activityLogs: SupportTicketActivityLog[];
}

export interface TicketAttachmentInput {
  fileId?: string;
  url: string;
  filename: string;
  mimeType?: string;
  sizeBytes?: number;
}

export interface CreateTicketRequest {
  category: SupportTicketCategory;
  subject: string;
  body: string;
  priority?: SupportTicketPriority;
  refType?: string;
  refId?: string;
  orderGroupId?: string;
  orderItemId?: string;
  productId?: string;
  vendorId?: string;
  attachments?: TicketAttachmentInput[];
}

export interface UpdateTicketRequest {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  assignedToId?: string | null;
  vendorId?: string | null;
  tags?: string[];
}

export interface EscalateTicketRequest {
  reason: string;
  priority?: SupportTicketPriority;
}

export interface VendorResolveTicketRequest {
  resolutionNote: string;
}

export interface ListTicketsQuery {
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  category?: SupportTicketCategory;
  assignedToId?: string;
  vendorId?: string;
  orderGroupId?: string;
  productId?: string;
  q?: string;
  slaState?: 'BREACHED' | 'AT_RISK' | 'HEALTHY';
  limit?: number;
}

export interface VendorSupportMetrics {
  vendorId: string;
  vendorName: string | null;
  openCount: number;
  inProgressCount: number;
  resolvedCount: number;
  escalatedCount: number;
  slaBreachedCount: number;
  avgFirstResponseMinutes: number | null;
  avgResolutionHours: number | null;
}

export interface SupportFaq {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export interface SupportHelpCategory {
  key: SupportTicketCategory;
  label: string;
  description: string;
  icon: string | null;
}

export interface SupportContactInfo {
  email: string;
  phone: string | null;
  hours: string | null;
  address: string | null;
}

// ── Product Queries & Conversations (unchanged) ──────────────────────────────

export interface ProductQuery {
  id: string;
  productId: string;
  customerId: string;
  question: string;
  answer: string | null;
  answeredById: string | null;
  vendorId: string | null;
  status: 'PENDING' | 'PUBLISHED' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
}

export type PublicProductQuery = Pick<
  ProductQuery,
  'id' | 'question' | 'answer' | 'createdAt' | 'updatedAt'
>;

export interface CreateProductQueryRequest {
  productId: string;
  question: string;
}

export interface AnswerProductQueryRequest {
  answer: string;
  status?: 'PUBLISHED' | 'REJECTED';
}

export type ProductConversationStatus = 'OPEN' | 'RESOLVED' | 'CLOSED';

export interface ProductConversationMessage {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  isRead: boolean;
  createdAt: string;
}

export interface ProductConversation {
  id: string;
  productId: string;
  buyerId: string;
  vendorId: string;
  subject: string;
  status: ProductConversationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ProductConversationDetail extends ProductConversation {
  messages: ProductConversationMessage[];
}

export interface CreateProductConversationRequest {
  productId: string;
  subject: string;
  initialMessage: string;
}

export interface ReplyProductConversationRequest {
  body: string;
}

// ── Contact Messages ─────────────────────────────────────────────────────────

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  status: 'PENDING' | 'READ' | 'REPLIED';
  createdAt: string;
  updatedAt: string;
}

export interface CreateContactMessageRequest {
  name: string;
  email: string;
  phone?: string;
  message: string;
}

export function supportEndpoints(http: AxiosInstance) {
  return {
    // ── Public help (no auth) ─────────────────────────────────────────────
    faqs: () => http.get<SupportFaq[]>('/support/faqs').then((r) => r.data),
    helpCategories: () =>
      http.get<SupportHelpCategory[]>('/support/help-categories').then((r) => r.data),
    contactInfo: () => http.get<SupportContactInfo>('/support/contact-info').then((r) => r.data),

    // ── Customer self-service ─────────────────────────────────────────────
    create: (body: CreateTicketRequest) =>
      http.post<SupportTicketDetail>('/support/tickets', body).then((r) => r.data),
    list: (params?: ListTicketsQuery) =>
      http.get<SupportTicket[]>('/support/tickets', { params }).then((r) => r.data),
    get: (ticketId: string) =>
      http.get<SupportTicketDetail>(`/support/tickets/${ticketId}`).then((r) => r.data),
    /** The customer confirms their issue is sorted. */
    close: (ticketId: string) =>
      http.post<SupportTicket>(`/support/tickets/${ticketId}/close`).then((r) => r.data),
    reply: (
      ticketId: string,
      body: { body: string; attachments?: TicketAttachmentInput[]; internal?: boolean },
    ) =>
      http
        .post<SupportTicketMessage>(`/support/tickets/${ticketId}/messages`, body)
        .then((r) => r.data),
    addAttachments: (ticketId: string, attachments: TicketAttachmentInput[]) =>
      http
        .post<
          SupportTicketAttachment[]
        >(`/support/tickets/${ticketId}/attachments`, { attachments })
        .then((r) => r.data),

    // ── Vendor (auto-scoped to caller's vendor row) ───────────────────────
    vendor: {
      list: (params?: ListTicketsQuery) =>
        http.get<SupportTicket[]>('/vendor/support/tickets', { params }).then((r) => r.data),
      get: (ticketId: string) =>
        http.get<SupportTicketDetail>(`/vendor/support/tickets/${ticketId}`).then((r) => r.data),
      reply: (
        ticketId: string,
        body: { body: string; attachments?: TicketAttachmentInput[]; internal?: boolean },
      ) =>
        http
          .post<SupportTicketMessage>(`/vendor/support/tickets/${ticketId}/messages`, body)
          .then((r) => r.data),
      resolve: (ticketId: string, body: VendorResolveTicketRequest) =>
        http
          .post<SupportTicket>(`/vendor/support/tickets/${ticketId}/resolve`, body)
          .then((r) => r.data),
      addAttachments: (ticketId: string, attachments: TicketAttachmentInput[]) =>
        http
          .post<
            SupportTicketAttachment[]
          >(`/vendor/support/tickets/${ticketId}/attachments`, { attachments })
          .then((r) => r.data),
    },

    // ── Admin ─────────────────────────────────────────────────────────────
    adminList: (params?: ListTicketsQuery) =>
      http.get<SupportTicket[]>('/admin/support/tickets', { params }).then((r) => r.data),
    adminGet: (ticketId: string) =>
      http.get<SupportTicketDetail>(`/admin/support/tickets/${ticketId}`).then((r) => r.data),
    adminCount: () =>
      http.get<{ count: number }>('/admin/support/tickets/count').then((r) => r.data),
    adminUpdate: (ticketId: string, body: UpdateTicketRequest) =>
      http.patch<SupportTicket>(`/admin/support/tickets/${ticketId}`, body).then((r) => r.data),
    adminEscalate: (ticketId: string, body: EscalateTicketRequest) =>
      http
        .post<SupportTicket>(`/admin/support/tickets/${ticketId}/escalate`, body)
        .then((r) => r.data),
    adminReply: (
      ticketId: string,
      body: { body: string; attachments?: TicketAttachmentInput[]; internal?: boolean },
    ) =>
      http
        .post<SupportTicketMessage>(`/admin/support/tickets/${ticketId}/messages`, body)
        .then((r) => r.data),
    adminVendorMetrics: () =>
      http.get<VendorSupportMetrics[]>('/admin/support/vendor-metrics').then((r) => r.data),

    // ── Product Queries ───────────────────────────────────────────────────
    productQueries: {
      listPublic: (productId: string) =>
        http
          .get<PublicProductQuery[]>(`/support/product-queries/public/${productId}`)
          .then((r) => r.data),
      listAdmin: () =>
        http.get<ProductQuery[]>('/admin/support/product-queries').then((r) => r.data),
      listVendor: () =>
        http.get<ProductQuery[]>('/vendor/support/product-queries').then((r) => r.data),
      create: (body: CreateProductQueryRequest) =>
        http.post<ProductQuery>('/support/product-queries', body).then((r) => r.data),
      answer: (queryId: string, body: AnswerProductQueryRequest) =>
        http
          .post<ProductQuery>(`/support/product-queries/${queryId}/answer`, body)
          .then((r) => r.data),
    },

    // ── Product Conversations ─────────────────────────────────────────────
    productConversations: {
      listMy: () =>
        http.get<ProductConversation[]>('/support/product-conversations').then((r) => r.data),
      listVendor: () =>
        http
          .get<ProductConversation[]>('/vendor/support/product-conversations')
          .then((r) => r.data),
      listAdmin: () =>
        http.get<ProductConversation[]>('/admin/support/product-conversations').then((r) => r.data),
      get: (conversationId: string) =>
        http
          .get<ProductConversationDetail>(`/support/product-conversations/${conversationId}`)
          .then((r) => r.data),
      create: (body: CreateProductConversationRequest) =>
        http
          .post<ProductConversationDetail>('/support/product-conversations', body)
          .then((r) => r.data),
      reply: (conversationId: string, body: ReplyProductConversationRequest) =>
        http
          .post<ProductConversationMessage>(
            `/support/product-conversations/${conversationId}/messages`,
            body,
          )
          .then((r) => r.data),
    },

    // ── Contact Messages ──────────────────────────────────────────────────
    contacts: {
      create: (body: CreateContactMessageRequest) =>
        http.post<ContactMessage>('/support/contacts', body).then((r) => r.data),
      listAll: () => http.get<ContactMessage[]>('/admin/support/contacts').then((r) => r.data),
      updateStatus: (contactId: string, status: 'PENDING' | 'READ' | 'REPLIED') =>
        http
          .patch<ContactMessage>(`/admin/support/contacts/${contactId}`, { status })
          .then((r) => r.data),
    },
  };
}

export type SupportEndpoints = ReturnType<typeof supportEndpoints>;
